import React, { useState } from 'react';
import DropZone from './components/DropZone';
import ProgressView from './components/ProgressView';
import { processArchive } from './utils/docxProcessor';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLayers, FiPrinter, FiCheckSquare, FiFileText, FiShield, FiBriefcase } = FiIcons;

export default function App() {
  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(false);

  const addLog = (msg) => {
    setLogs(prev => [...prev, msg]);
  };

  const handleFileSelect = async (selectedFile) => {
    setStatus('processing');
    setProgress(0);
    setLogs([]);
    setError(false);
    try {
      await processArchive(
        selectedFile,
        (p) => setProgress(p),
        (msg) => addLog(msg)
      );
      setStatus('done');
    } catch (err) {
      setError(true);
      setStatus('error');
    }
  };

  const reset = () => {
    setStatus('idle');
    setProgress(0);
    setLogs([]);
    setError(false);
  };

  return (
    <div className="min-h-screen bg-brand-light font-sans text-brand-dark">
      <header className="bg-brand-dark border-b border-brand-dark/10 sticky top-0 z-10 shadow-md">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-brand-accent p-2.5 rounded-lg shadow-sm">
              <SafeIcon icon={FiPrinter} className="w-6 h-6 text-brand-dark" />
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-2xl tracking-tight text-white leading-none">FullyDocs</span>
              <span className="text-brand-accent text-xs font-semibold uppercase tracking-widest mt-1">Print Conversion</span>
            </div>
          </div>
          <div className="hidden sm:block text-xs font-medium text-slate-400 uppercase tracking-widest">
            Professional Manual Automation
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-black tracking-tight text-brand-dark sm:text-6xl mb-6">
            Professional Binder <span className="text-brand-accent">Referencing</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Automatically transform digital hyperlinks into professional physical references.
          </p>
        </div>

        <div className="mb-20">
          {status === 'idle' ? (
            <div className="space-y-16">
              <DropZone onFileSelect={handleFileSelect} />
              
              <div className="max-w-4xl mx-auto bg-white p-10 rounded-3xl shadow-xl border border-brand-dark/5">
                <div className="prose prose-slate max-w-none text-slate-600 space-y-6">
                  <p className="text-lg leading-relaxed">
                    Upload your ZIP file and the <strong>FullyDocs Print Conversion Tool</strong> prepares your document set for physical printing. It identifies and converts internal hyperlinks into clear printed references, helping readers navigate the manual without relying on clickable links.
                  </p>
                  <p className="text-lg leading-relaxed">
                    Ideal for O&M manuals, health and safety files, technical submissions, handover packs and compliance documentation, the tool helps bridge the gap between digital document control and a professional hard-copy deliverable.
                  </p>
                  <div className="bg-brand-light/50 p-6 rounded-2xl border-l-4 border-brand-accent">
                    <p className="italic font-medium text-brand-dark">
                      "Your print-ready pack is designed to make documents easier to follow on paper, with references presented clearly and consistently throughout."
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 mt-12 pt-12 border-t border-slate-100">
                  <div className="space-y-4">
                    <h3 className="font-bold text-brand-dark text-lg flex items-center gap-2">
                      <SafeIcon icon={FiCheckSquare} className="text-brand-accent" /> Key Features
                    </h3>
                    <ul className="space-y-3">
                      {[
                        "Upload ZIP-based document packs",
                        "Convert hyperlink-led navigation into printed references",
                        "Suitable for complex technical and construction documentation",
                        "Creates a cleaner, more usable hard-copy manual",
                        "Built for O&M, H&S, handover and compliance documents"
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-brand-accent mt-1.5 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex flex-col justify-center bg-brand-dark text-white p-8 rounded-2xl">
                    <p className="text-brand-accent font-bold text-xl mb-2">Ready to print?</p>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      FullyDocs helps ensure your documents work just as well in print as they do online.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <ProgressView progress={progress} logs={logs} error={error} onReset={reset} />
          )}
        </div>

        {status === 'idle' && (
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 hover:border-brand-accent transition-colors">
              <div className="bg-brand-light w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <SafeIcon icon={FiFileText} className="w-7 h-7 text-brand-dark" />
              </div>
              <h3 className="font-bold text-brand-dark mb-2 text-lg">O&M Manuals</h3>
              <p className="text-slate-500 text-sm leading-relaxed">Streamline operation and maintenance manuals with precise physical tab mapping.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 hover:border-brand-accent transition-colors">
              <div className="bg-brand-light w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <SafeIcon icon={FiShield} className="w-7 h-7 text-brand-dark" />
              </div>
              <h3 className="font-bold text-brand-dark mb-2 text-lg">Compliance Ready</h3>
              <p className="text-slate-500 text-sm leading-relaxed">Perfect for H&S files and handover packs where physical accessibility is mandatory.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 hover:border-brand-accent transition-colors">
              <div className="bg-brand-light w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                <SafeIcon icon={FiBriefcase} className="w-7 h-7 text-brand-dark" />
              </div>
              <h3 className="font-bold text-brand-dark mb-2 text-lg">Technical Packs</h3>
              <p className="text-slate-500 text-sm leading-relaxed">Convert complex digital technical submissions into organized hard-copy binders.</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}